import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
    Sector,
    type TooltipProps,
} from 'recharts'
import type { PieSectorDataItem } from 'recharts/types/polar/Pie'
import { useState } from 'react'
import { getDonutColor } from './donutColors'
import { TooltipFloater } from '@/components/atoms'
import { groupExcessItemsAsOthers } from './groupExcessItemsAsOthers'
import { EmissionData, LegendContent, TooltipEntry } from './types'
import { splitFormattedNumber } from '@/utils/splitFormattedNumber'
import { capitalizeFirstChar } from '@/utils/capitalizeFirstChar'

interface PieChartWithSummaryProps {
    data: EmissionData[]
    centerContent?: {
        title: string | React.ReactNode
        value: (total: number) => string | number | React.ReactNode
        unit: string | React.ReactNode
    }
    legendContent: LegendContent
    tooltipContent: (data: EmissionData[]) => TooltipEntry[]
    total: number
}

const renderActiveShape = (props: PieSectorDataItem) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
        props
    return (
        <Sector
            cx={cx}
            cy={cy}
            innerRadius={(innerRadius ?? 0) - 5}
            outerRadius={(outerRadius ?? 0) + 5}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
            cornerRadius={4}
        />
    )
}

interface LegendProps {
    data: EmissionData[]
    value: (
        item: EmissionData,
        index: number
    ) => string | number | React.ReactNode
    title?: string | React.ReactNode
}

const Legend = ({ data, value, title }: LegendProps) => {
    const filteredData = data.filter((item) => item.value > 0)

    return (
        <div className="flex flex-shrink-0 flex-col justify-center">
            {title && (
                <div
                    className="text-heading-6 mb-2"
                    data-testid="timeline-date-range"
                >
                    {title}
                </div>
            )}
            <div className="flex flex-col gap-1">
                {filteredData.map((item, index) => (
                    <div
                        key={`legend-${item.name}-${String(index)}`}
                        data-testid="legend-item"
                        className="flex items-center rounded"
                    >
                        <div className="flex w-full flex-row items-baseline justify-between gap-6">
                            <div>
                                <span
                                    data-testid="legend-color"
                                    className="mr-2 inline-block h-3 w-3 flex-shrink-0 rounded-full"
                                    style={{
                                        backgroundColor: getDonutColor(index),
                                    }}
                                />
                                <span className="text-label-small font-medium">
                                    {capitalizeFirstChar(item.name)}
                                </span>
                            </div>
                            <span className="text-body-medium font-semibold">
                                {value(item, index)}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function PieChartWithSummary({
    data,
    centerContent,
    legendContent,
    tooltipContent,
    total,
}: PieChartWithSummaryProps) {
    // Sort data by value descending
    const sortedData = [...data].sort((a, b) => b.value - a.value)
    const [activeIndex, setActiveIndex] = useState<number | null>(null)
    const MAX_VISIBLE_ITEMS = 7

    // Group and sort data for chart slices
    const groupedData = groupExcessItemsAsOthers(sortedData, MAX_VISIBLE_ITEMS)

    const renderCustomTooltip = (props: TooltipProps<number, string>) => {
        const { active, payload, coordinate } = props
        if (
            active &&
            payload &&
            payload.length > 0 &&
            coordinate &&
            coordinate.x &&
            coordinate.y
        ) {
            const item = payload[0]?.payload as EmissionData & { fill: string }
            const index = groupedData.findIndex((d) => d.name === item.name)
            if (index === -1) return null

            const color = item.fill || getDonutColor(index)
            const tooltip = tooltipContent(groupedData)[index]

            return (
                <div
                    className="absolute"
                    style={{
                        left: coordinate.x + 10,
                        top: coordinate.y - 35,
                    }}
                >
                    <TooltipFloater
                        data-testid="tooltip-floater"
                        className="!max-w-none !p-0"
                    >
                        <div className="inline-block px-3 py-2">
                            <div className="mb-1 flex items-center">
                                <span
                                    className="mr-1.5 inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full font-bold"
                                    style={{ backgroundColor: color }}
                                />
                                <span className="text-sm font-bold">
                                    {capitalizeFirstChar(item.name)}
                                </span>
                            </div>
                            <div className="flex flex-row gap-4">
                                <span className="whitespace-nowrap text-xs font-medium">
                                    {tooltip.label} ({tooltip.unit})
                                </span>
                                <span className="text-xs font-semibold">
                                    {tooltip.value}
                                </span>
                                <span className="text-xs font-semibold">
                                    {tooltip.percentage}
                                </span>
                            </div>
                        </div>
                    </TooltipFloater>
                </div>
            )
        }
        return null
    }

    const handlePieHover = (index: number | null) => {
        setActiveIndex(index)
    }

    const { integerPart, decimalPart } = splitFormattedNumber(
        centerContent?.value(total) as string | number
    )

    return (
        <div className="flex items-center justify-center gap-12 self-stretch">
            <div
                className="relative flex aspect-[1/1] w-[600px] flex-col items-center justify-center gap-2 self-stretch"
                data-testid="donut-chart"
            >
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={groupedData}
                            cx="50%"
                            cy="50%"
                            innerRadius="70%"
                            outerRadius="100%"
                            dataKey="value"
                            paddingAngle={3}
                            startAngle={90}
                            endAngle={-270}
                            isAnimationActive={false}
                            activeIndex={activeIndex ?? -1}
                            activeShape={renderActiveShape}
                            cornerRadius={3}
                            onMouseEnter={(_, index) => {
                                handlePieHover(index)
                            }}
                            onMouseLeave={() => {
                                handlePieHover(null)
                            }}
                            minAngle={3}
                        >
                            {groupedData.map((_, index) => (
                                <Cell
                                    radius={0}
                                    key={`cell-${String(index)}`}
                                    fill={getDonutColor(index)}
                                />
                            ))}
                        </Pie>
                        <Tooltip
                            content={renderCustomTooltip}
                            wrapperStyle={{
                                zIndex: 9999,
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>

                {/* Center Content */}
                {centerContent && (
                    <div className="pointer-events-none absolute left-1/2 top-1/2 w-[65%] -translate-x-1/2 -translate-y-1/2 transform break-words text-center md:w-full">
                        <div
                            className="mb-1 text-base font-medium leading-[1.2]"
                            data-testid="donut-center-title"
                        >
                            {centerContent.title}
                        </div>
                        <div
                            className="mb-0.5 flex items-center justify-center whitespace-nowrap text-xl font-semibold leading-[1.6]"
                            data-testid="donut-center-value"
                        >
                            <span className="inline-block">{integerPart}</span>
                            {decimalPart && (
                                <span className="mt-0.5 text-base leading-[normal]">
                                    {'.' + decimalPart}
                                </span>
                            )}
                        </div>
                        <div
                            className="mb-1 text-base font-medium leading-[1.2]"
                            data-testid="donut-center-unit"
                        >
                            {centerContent.unit}
                        </div>
                    </div>
                )}
            </div>

            <Legend
                data={groupedData}
                value={legendContent.value}
                title={legendContent.title}
            />
        </div>
    )
}
